import { useEffect, useMemo } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { useSearchTikTokTracksAuthQuery } from 'src/Redux/tiktokTrackSearchApi'
import { getValueOrEmptyString } from 'src/Helpers/layoutHelpers'
import { convertTimeHandler, getValueOrZero } from 'src/Constants/functions'
import { usePageViewEvent } from 'src/Hooks/Analytics/useTrackEvents'
import { Track } from 'src/Redux/firstTrackApi'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { useGetSpotifyTrackByIdQuery } from 'src/Redux/spotifyTracksApi'

export interface FirstChosenTrackParams {
  [key: string]: string | undefined
  platform?: CampaignPlatformTypeEnum
  trackId?: string
}

const MILLISECONDS_PER_SECOND = 1000

export interface TikTokTrack extends Track {
  isOriginalSound?: boolean
  initialVideoCount?: number
  trackUrl?: string
}

export interface SpotifyTrack extends Track {
  artistSpotifyId?: string
}

export const useSelectedTrackAuth = (): {
  spotifyTrack: SpotifyTrack | null
  tikTokTrack: TikTokTrack | null
  loading: boolean
} => {
  const { trackAddedEventHandler } = usePageViewEvent()
  const navigate = useNavigate()
  const { platform, trackId } = useParams<FirstChosenTrackParams>()

  const {
    data: tiktokTracks,
    isLoading: isTiktokLoading,
    isError: isTiktokError,
  } = useSearchTikTokTracksAuthQuery(
    {
      searchText: `https://www.tiktok.com/music/-${trackId}`,
    },
    {
      skip:
        !trackId || !platform || platform !== CampaignPlatformTypeEnum.TIKTOK,
    },
  )
  const {
    data: spotifyTrackData,
    isLoading: isSpotifyLoading,
    isError: isSpotifyError,
  } = useGetSpotifyTrackByIdQuery(trackId || '', {
    skip:
      !trackId || !platform || platform !== CampaignPlatformTypeEnum.SPOTIFY,
  })

  const spotifyTrack = useMemo(() => {
    if (platform === CampaignPlatformTypeEnum.SPOTIFY) {
      return {
        trackId: spotifyTrackData?.id,
        trackName: spotifyTrackData?.trackName,
        artistName: spotifyTrackData?.trackAuthor,
        trackCoverUrl: spotifyTrackData?.trackImage,
        trackDuration: convertTimeHandler({
          value: spotifyTrackData?.trackDuration || 0,
          type: 'ms',
        }),
        trackPreviewUrl: spotifyTrackData?.previewUrl,
        trackDurationInMs: spotifyTrackData?.trackDuration || 0,
        artistSpotifyId: spotifyTrackData?.artistSpotifyId,
      }
    }
    return null
  }, [platform, spotifyTrackData])

  const tikTokTrack = useMemo(() => {
    if (platform === CampaignPlatformTypeEnum.TIKTOK) {
      return {
        trackId: tiktokTracks?.[0]?.trackTiktokId,
        trackName: tiktokTracks?.[0]?.trackTitle,
        artistName: tiktokTracks?.[0]?.trackAuthorName,
        trackCoverUrl: tiktokTracks?.[0]?.trackCoverUrl,
        trackDuration: convertTimeHandler({
          value: tiktokTracks?.[0].trackDuration || 0,
          type: 's',
        }),
        trackPreviewUrl: tiktokTracks?.[0]?.trackPlayUrl,
        trackDurationInMs:
          (tiktokTracks?.[0].trackDuration || 0) * MILLISECONDS_PER_SECOND,
        originalSound: tiktokTracks?.[0],
        initialVideoCount: tiktokTracks?.[0].initialVideoCount,
        trackUrl: tiktokTracks?.[0]?.trackUrl,
        isOriginalSound: tiktokTracks?.[0].isOriginalSound,
      }
    }
    return null
  }, [platform, tiktokTracks])

  const track =
    platform === CampaignPlatformTypeEnum.TIKTOK ? tikTokTrack : spotifyTrack
  useEffect(() => {
    if (track?.trackId && platform) {
      trackAddedEventHandler({
        artistName: getValueOrEmptyString(track.artistName),
        platformType: platform,
        trackDuration: getValueOrZero(track.trackDurationInMs),
        trackId: getValueOrEmptyString(track.trackId),
        trackName: getValueOrEmptyString(track.trackName),
      })
    }
  }, [
    platform,
    track?.artistName,
    track?.trackDurationInMs,
    track?.trackId,
    track?.trackName,
    trackAddedEventHandler,
  ])

  useEffect(() => {
    if (isSpotifyError || isTiktokError) {
      navigate(`${ArtistRoutesEnum.ChooseCreateCampaignPlatform}/${platform}`, {
        replace: true,
      })
    }
  }, [isSpotifyError, isTiktokError, navigate, platform])

  return {
    spotifyTrack,
    tikTokTrack,
    loading: isSpotifyLoading || isTiktokLoading,
  }
}
